// Manager mobile "Aanvragen" — 3 tabs: Verlof (pending leaves), Ruilen (accepted swaps waiting for manager approval), Uren (submitted time approvals).
// One-tap approve / reject on every row.
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import PaperPlaneEmpty from "@/components/PaperPlaneEmpty";
import SwipeAction from "@/components/SwipeAction";
import { avatarBg } from "@/lib/roleColors";
import { ClipboardList, Repeat, Clock, Check, X, Inbox, Calendar } from "lucide-react";

function initials(n = "") { return n.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join(""); }
function avatarBgLocal(n) { return avatarBg(n, "employee"); }
const _unused_hsl = null;
function shortDate(iso) { if (!iso) return "—"; return new Date(iso).toLocaleDateString("nl", { day: "numeric", month: "short" }); }

export default function ManagerRequests() {
  const { t } = useI18n();
  const [tab, setTab] = useState("leave");
  const [leaves, setLeaves] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [times, setTimes] = useState([]);

  const load = async () => {
    const [l, s, tm] = await Promise.all([
      api.get(`/leave-requests?status=pending`),
      api.get(`/swap-requests`),
      api.get(`/time-approvals?status=submitted`),
    ]);
    setLeaves(l.data || []);
    setSwaps((s.data || []).filter((w) => w.status === "accepted"));
    setTimes(tm.data || []);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const decideLeave = async (id, status) => {
    try { await api.post(`/leave-requests/${id}/decide`, { status }); toast.success(t(status === "approved" ? "leave_approved" : "leave_rejected")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const decideSwap = async (id, action) => {
    try { await api.post(`/swap-requests/${id}/action`, { action }); toast.success(t("saved")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const decideTime = async (id, status) => {
    try { await api.post(`/time-approvals/${id}/decide`, { status }); toast.success(t(status === "approved" ? "hours_approved" : "hours_rejected")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  const counts = { leave: leaves.length, swap: swaps.length, time: times.length };
  const total = counts.leave + counts.swap + counts.time;

  return (
    <div className="pb-24" data-testid="manager-requests-page">
      <PageHeader
        eyebrow={t("requests_eyebrow")}
        title={t("requests_title")}
        subtitle={t("requests_subtitle")}
      />

      {/* Summary */}
      <div
        className="card p-5 mb-4 flex items-center gap-4"
        style={{ background: "linear-gradient(135deg, hsl(var(--primary-soft)) 0%, hsl(var(--surface)) 60%)" }}
        data-testid="requests-summary"
      >
        <div
          className="w-12 h-12 rounded-2xl grid place-items-center shrink-0 text-white"
          style={{ background: "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(217 91% 45%) 100%)", boxShadow: "0 12px 28px -10px hsl(var(--primary) / 0.55)" }}
        >
          <Inbox size={20} strokeWidth={2.2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[22px] font-bold text-[hsl(var(--text-strong))] leading-none tabular-nums">{total}</div>
          <div className="text-[12px] font-semibold text-[hsl(var(--text-muted))] mt-1">
            {total === 1 ? t("requests_one_pending") : t("requests_many_pending").replace("{n}", total)}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-3 p-1 rounded-2xl bg-[hsl(var(--muted))]" data-testid="requests-tabs">
        {[
          { k: "leave", label: t("requests_tab_leave"), Icon: ClipboardList, n: counts.leave },
          { k: "swap", label: t("requests_tab_swap"), Icon: Repeat, n: counts.swap },
          { k: "time", label: t("requests_tab_time"), Icon: Clock, n: counts.time },
        ].map(({ k, label, Icon, n }) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`tab-${k}`}
            className={`relative flex-1 flex items-center justify-center gap-1.5 h-10 rounded-xl text-[12.5px] font-semibold transition-colors ${
              tab === k ? "bg-[hsl(var(--surface))] text-[hsl(var(--text-strong))] shadow-sm" : "text-[hsl(var(--text-muted))]"
            }`}
          >
            <Icon size={13} strokeWidth={2.2} />
            <span className="truncate">{label}</span>
            {n > 0 && (
              <span className={`text-[10px] font-bold px-1.5 rounded-full min-w-[16px] h-4 grid place-items-center ${
                tab === k ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--danger))] text-white"
              }`}>{n}</span>
            )}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab === "leave" && (
          <motion.div key="leave" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="flex flex-col gap-2" data-testid="leave-list">
            {leaves.length === 0 && <Empty Icon={ClipboardList} text={t("requests_no_leave")} />}
            {leaves.length > 0 && (
              <div className="text-[11px] text-[hsl(var(--text-muted))] text-center italic mb-1" data-testid="swipe-hint">
                {t("swipe_hint") || "Veeg naar rechts om goed te keuren, naar links om af te wijzen"}
              </div>
            )}
            {leaves.map((l) => (
              <SwipeAction
                key={l._id}
                testid={`swipe-leave-${l._id}`}
                onApprove={() => decideLeave(l._id, "approved")}
                onReject={() => decideLeave(l._id, "rejected")}
              >
                <div className="card p-3 flex items-start gap-3" data-testid={`leave-${l._id}`}>
                  <div className="w-10 h-10 rounded-xl grid place-items-center text-white text-[13px] font-bold shrink-0" style={{ background: avatarBg(l.employee_name) }}>
                    {initials(l.employee_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-[hsl(var(--text-strong))] truncate">{l.employee_name}</div>
                    <div className="text-[11.5px] text-[hsl(var(--text-muted))] flex items-center gap-1 mt-0.5">
                      <Calendar size={10} /> <span className="font-mono">{shortDate(l.start_date)} – {shortDate(l.end_date)}</span>
                      {l.leave_type && <span className="opacity-60">· {t(l.leave_type)}</span>}
                    </div>
                    {l.reason && <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1 italic truncate">"{l.reason}"</div>}
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button onClick={() => decideLeave(l._id, "approved")} className="w-9 h-9 rounded-lg bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] grid place-items-center" data-testid={`approve-leave-${l._id}`}><Check size={14} strokeWidth={2.5} /></button>
                    <button onClick={() => decideLeave(l._id, "rejected")} className="w-9 h-9 rounded-lg bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] grid place-items-center" data-testid={`reject-leave-${l._id}`}><X size={14} strokeWidth={2.5} /></button>
                  </div>
                </div>
              </SwipeAction>
            ))}
          </motion.div>
        )}

        {tab === "swap" && (
          <motion.div key="swap" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="flex flex-col gap-2" data-testid="swap-list">
            {swaps.length === 0 && <Empty text={t("requests_no_swap")} />}
            {swaps.map((w) => (
              <div key={w._id} className="card p-3" data-testid={`swap-${w._id}`}>
                <div className="flex items-center gap-2 text-[11.5px] text-[hsl(var(--text-muted))] mb-2 font-mono">
                  <Clock size={11} /> {w.shift_summary}
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg grid place-items-center text-white text-[11px] font-bold" style={{ background: avatarBg(w.from_employee_name) }}>{initials(w.from_employee_name)}</div>
                  <div className="text-[12px] text-[hsl(var(--text-muted))]">→</div>
                  <div className="w-8 h-8 rounded-lg grid place-items-center text-white text-[11px] font-bold" style={{ background: avatarBg(w.target_employee_name || "") }}>{initials(w.target_employee_name || "?")}</div>
                  <div className="text-[12px] text-[hsl(var(--text-strong))] font-semibold truncate ml-1">
                    <b>{w.from_employee_name}</b> → <b>{w.target_employee_name}</b>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => decideSwap(w._id, "approve")} className="btn btn-primary flex-1 !h-9 !text-[12px]" data-testid={`approve-swap-${w._id}`}><Check size={13} /> {t("approve")}</button>
                  <button onClick={() => decideSwap(w._id, "decline")} className="btn btn-ghost !h-9 !text-[12px]" data-testid={`reject-swap-${w._id}`}><X size={13} /> {t("reject")}</button>
                </div>
              </div>
            ))}
          </motion.div>
        )}

        {tab === "time" && (
          <motion.div key="time" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="flex flex-col gap-2" data-testid="time-list">
            {times.length === 0 && <Empty text={t("requests_no_time")} />}
            {times.map((r) => (
              <SwipeAction
                key={r._id}
                testid={`swipe-time-${r._id}`}
                onApprove={() => decideTime(r._id, "approved")}
                onReject={() => decideTime(r._id, "rejected")}
              >
                <div className="card p-3 flex items-start gap-3" data-testid={`time-${r._id}`}>
                  <div className="w-10 h-10 rounded-xl grid place-items-center text-white text-[13px] font-bold shrink-0" style={{ background: avatarBg(r.employee_name) }}>
                    {initials(r.employee_name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-semibold text-[hsl(var(--text-strong))] truncate">{r.employee_name}</div>
                    <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-0.5 font-mono">
                      {shortDate(r.shift_date)} · {r.actual_start}–{r.actual_end}
                    </div>
                    {r.note && <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-1 italic truncate">"{r.note}"</div>}
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button onClick={() => decideTime(r._id, "approved")} className="w-9 h-9 rounded-lg bg-[hsl(var(--success-soft))] text-[hsl(var(--success))] grid place-items-center" data-testid={`approve-time-${r._id}`}><Check size={14} strokeWidth={2.5} /></button>
                    <button onClick={() => decideTime(r._id, "rejected")} className="w-9 h-9 rounded-lg bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] grid place-items-center" data-testid={`reject-time-${r._id}`}><X size={14} strokeWidth={2.5} /></button>
                  </div>
                </div>
              </SwipeAction>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Empty({ text }) {
  return (
    <div className="py-8 text-center">
      <PaperPlaneEmpty size={110} />
      <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))] mt-2">{text}</div>
    </div>
  );
}
